import React, { useCallback } from 'react';
import { TextField as MuiTextField, useTheme } from '@mui/material';
import { styled } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import ClearIcon from '@mui/icons-material/Clear';
import InputAdornment from '@mui/material/InputAdornment';
import useSafeState from '@hooks/useSafeState';

const StyledTextField = styled(MuiTextField)(({ theme }) => ({
  backgroundColor: theme.palette?.textField?.bg || theme.palette.background.paper,
  borderRadius: 'var(--radius-8)',
  boxShadow: 'var(--shadow-soft)',
  color: theme.palette?.textField?.text || theme.palette.text.primary,

  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: theme.palette?.textField?.border || theme.palette.divider,
      borderWidth: 1,
    },
    '&:hover fieldset': {
      borderColor: theme.palette?.textField?.focusBorder || theme.palette.primary.main,
      borderWidth: 1,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette?.textField?.focusBorder || theme.palette.primary.main,
      borderWidth: 1,
    },
    '&.Mui-disabled': {
      backgroundColor: theme.palette?.textField?.disabledBg || theme.palette.action.disabledBackground,
      color: theme.palette?.textField?.disabledText || theme.palette.text.disabled,
    },
  },

  '& .MuiFormLabel-root': {
    color: theme.palette?.textField?.label || theme.palette.text.secondary,
    '&.MuiInputLabel-shrink': {
      transform: 'translate(14px, -8px) scale(0.80)',
      backgroundColor: theme.palette.background.paper,
      padding: '0 4px',
    },
  },

  '& .MuiOutlinedInput-input': {
    color: theme.palette?.textField?.text || theme.palette.text.primary,
  },

  '&.Mui-error .MuiOutlinedInput-root fieldset': {
    borderColor: theme.palette?.textField?.error || theme.palette.error.main,
  },
}));

const MyTextField = ({
  label,
  name,
  value,
  onChange,
  disabled = false,
  type = 'text',
  required = false,
  inputProps = {},
  onBlur,
  onFocus,
  onKeyUp,
  labelStyles = {},
  InputProps = {},
  clearable = true,
  ...props
}) => {
  const theme = useTheme();
  const [isHovered, setIsHovered] = useSafeState(false);
  const {
    sx: sxProp = {},
    InputLabelProps: inputLabelPropsProp = {},
    ...restProps
  } = props;

  const mergedLabelStyles = {
    color: theme.palette?.textField?.label || theme.palette.text.secondary,
    ...labelStyles,
  };

  const handleClear = useCallback(() => {
    if (onChange) {
      onChange({ target: { name, value: '' } });
    }
  }, [name, onChange]);

  const getEndAdornment = () => {
    if (InputProps && Object.prototype.hasOwnProperty.call(InputProps, 'endAdornment')) {
      return InputProps.endAdornment;
    }
    if (!clearable) return null;

    const showClear = Boolean(isHovered && value);

    return (
      <InputAdornment
        position="end"
        sx={{
          minWidth: 34,
          justifyContent: 'center',
        }}
      >
        <IconButton
          aria-label="Limpiar campo"
          onClick={handleClear}
          edge="end"
          size="small"
          tabIndex={-1}
          disabled={!showClear}
          sx={{
            opacity: showClear ? 1 : 0,
            pointerEvents: showClear ? 'auto' : 'none',
            transition: 'opacity 120ms ease',
          }}
        >
          <ClearIcon fontSize="small" />
        </IconButton>
      </InputAdornment>
    );
  };

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ width: '100%' }}
    >
      <StyledTextField
        label={label}
        name={name}
        value={value}
        onChange={onChange}
        disabled={disabled}
        type={type}
        required={required}
        variant="outlined"
        size="small"
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyUp={onKeyUp}
        fullWidth
        inputProps={inputProps}
        sx={{
          '& .MuiFormLabel-root': mergedLabelStyles,
          ...sxProp,
        }}
        InputLabelProps={{
          shrink: true,
          ...inputLabelPropsProp,
        }}
        InputProps={{
          ...InputProps,
          endAdornment: getEndAdornment(),
        }}
        {...restProps}
      />
    </div>
  );
};

export default MyTextField;
